import React, { useState } from "react";
import { CircularProgress, IconButton, InputAdornment } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { toast } from "react-toastify";
import * as S from "./styles";
import { updateProfile } from "../../utils/services/modules/user";
import {
  getApiError,
  getApiErrorField,
} from "../../utils/services/database";

interface IPasswordForm {
  user: { userName: string; token: string };
}

type Field = "oldPassword" | "newPassword" | "confirmPassword";
type Errors = Partial<Record<Field, string>>;

const MIN_LENGTH = 6;

const PasswordForm: React.FC<IPasswordForm> = ({ user }) => {
  const [values, setValues] = useState<Record<Field, string>>({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [visible, setVisible] = useState<Partial<Record<Field, boolean>>>({});
  const [errors, setErrors] = useState<Errors>({});
  const [saving, setSaving] = useState(false);

  const filled = Object.values(values).some(Boolean);

  const change = (field: Field, value: string) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validate = (): Errors => {
    const { oldPassword, newPassword, confirmPassword } = values;
    const next: Errors = {};
    if (!oldPassword) next.oldPassword = "Informe a senha atual";
    if (newPassword.length < MIN_LENGTH) {
      next.newPassword = `Use pelo menos ${MIN_LENGTH} caracteres`;
    } else if (newPassword === oldPassword) {
      next.newPassword = "A nova senha precisa ser diferente da atual";
    }
    if (!next.newPassword && confirmPassword !== newPassword) {
      next.confirmPassword = "As senhas não conferem";
    }
    return next;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    // o botão não é desabilitado (no Chrome isso travava o teclado): a
    // proteção contra clique duplo fica aqui
    if (saving || !filled) return;

    const nextErrors = validate();
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    setSaving(true);
    try {
      await updateProfile(
        { oldPassword: values.oldPassword, newPassword: values.newPassword },
        user.token,
      );
      setValues({ oldPassword: "", newPassword: "", confirmPassword: "" });
      setVisible({});
      toast.success("Senha alterada com sucesso!");
    } catch (error) {
      const field = getApiErrorField(error);
      const message = getApiError(
        error,
        "Não foi possível alterar a senha. Tente novamente.",
      );
      if (field === "oldPassword" || field === "newPassword") {
        setErrors({ [field]: message });
      } else {
        toast.error(message);
      }
    } finally {
      setSaving(false);
    }
  };

  const passwordField = (
    field: Field,
    label: string,
    autoComplete: string,
    hint?: string,
  ) => (
    <S.Field>
      <S.FieldLabel htmlFor={`settings-${field}`}>{label}</S.FieldLabel>
      <S.Input
        id={`settings-${field}`}
        type={visible[field] ? "text" : "password"}
        value={values[field]}
        onChange={(e) => change(field, e.target.value)}
        autoComplete={autoComplete}
        inputProps={{ maxLength: 100 }}
        error={!!errors[field]}
        helperText={errors[field] ?? hint}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                edge="end"
                aria-label={visible[field] ? "Ocultar senha" : "Mostrar senha"}
                onClick={() =>
                  setVisible((prev) => ({ ...prev, [field]: !prev[field] }))
                }
              >
                {visible[field] ? (
                  <VisibilityOffIcon fontSize="small" />
                ) : (
                  <VisibilityIcon fontSize="small" />
                )}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    </S.Field>
  );

  return (
    <S.FormCard onSubmit={handleSubmit} noValidate>
      {/* ajuda o gerenciador de senhas do navegador a saber de qual conta é */}
      <input
        type="text"
        name="username"
        autoComplete="username"
        value={user.userName}
        readOnly
        hidden
      />
      {passwordField("oldPassword", "Senha atual", "current-password")}
      {passwordField(
        "newPassword",
        "Nova senha",
        "new-password",
        `Mínimo de ${MIN_LENGTH} caracteres`,
      )}
      {passwordField("confirmPassword", "Confirmar nova senha", "new-password")}

      <S.SubmitButton type="submit" $inactive={!filled} aria-disabled={!filled}>
        {saving ? (
          <CircularProgress size={20} color="inherit" />
        ) : (
          "Alterar senha"
        )}
      </S.SubmitButton>
    </S.FormCard>
  );
};

export default PasswordForm;
